import type { DrawingDocument, PageSetup } from '../model';
import { renderableAnnotations } from './annotationRendering';
import { documentToAtoms, documentToLines } from './drawingDocumentProjections';
import { annotationExtentPoints } from '../rendering/annotationExtents';
import { pageOf } from './page';

// #805 -- the page grows with the content (docs/model/drawing-ui-viewport.md, owner decision).
// The page is whole copies of its base page (the preset or custom size): when content crosses an
// edge, the page grows by one base page toward that side (more if the content reaches further), and
// it shrinks back to the smallest such page that still holds everything (never below one page). Content never moves -- growing left or up extends
// the page around it. Everything here is in drawing units.

export interface PageExtent {
  readonly left: number;
  readonly top: number;
  readonly columns: number;
  readonly rows: number;
}

export interface ContentBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export const SINGLE_PAGE: PageExtent = { left: 0, top: 0, columns: 1, rows: 1 };

// Room around an atom for its label, so an atom sitting on the edge still has its symbol inside.
export const ATOM_LABEL_MARGIN = 12;

// A content edge this close past the page edge still counts as inside (rounding in saved files).
const EDGE_TOLERANCE = 0.5;

// One axis: the base page [0, 1) plus one more base page for each page-width (or height) the
// content reaches past either edge -- the page grows one page at a time (owner, October 5: doubling
// grew too fast). Returns the start and the count, in base pages.
export function growAxis(min: number, max: number, base: number): { start: number; count: number } {
  const start = Math.min(0, Math.floor((min + EDGE_TOLERANCE) / base));
  const end = Math.max(1, Math.ceil((max - EDGE_TOLERANCE) / base));
  return { start, count: end - start };
}

export function pageExtentFor(
  base: { readonly width: number; readonly height: number },
  bounds: ContentBounds | null
): PageExtent {
  if (!bounds || !(base.width > 0) || !(base.height > 0)) return SINGLE_PAGE;
  const x = growAxis(bounds.minX, bounds.maxX, base.width);
  const y = growAxis(bounds.minY, bounds.maxY, base.height);
  return { left: x.start, top: y.start, columns: x.count, rows: y.count };
}

export const extentOf = (page: PageSetup): PageExtent => page.extent ?? SINGLE_PAGE;
export const isSinglePage = (extent: PageExtent) =>
  extent.left === 0 && extent.top === 0 && extent.columns === 1 && extent.rows === 1;

// The grown page's rectangle in drawing units.
export function pageRect(page: PageSetup): { x: number; y: number; width: number; height: number } {
  const extent = extentOf(page);
  return {
    x: extent.left * page.width,
    y: extent.top * page.height,
    width: extent.columns * page.width,
    height: extent.rows * page.height
  };
}

export function withExtent(page: PageSetup, extent: PageExtent): PageSetup {
  const { extent: _previous, ...rest } = page;
  return isSinglePage(extent) ? rest : { ...rest, extent };
}

export function boundsOfPoints(points: readonly { readonly x: number; readonly y: number }[]): ContentBounds | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { minX, minY, maxX, maxY };
}

// Atom positions widened by the label margin, plus the visual extent of every annotation.
export function contentBounds(
  atomPositions: readonly { readonly x: number; readonly y: number }[],
  annotationPoints: readonly { readonly x: number; readonly y: number }[]
): ContentBounds | null {
  const points = [
    ...atomPositions.flatMap((p) => [
      { x: p.x - ATOM_LABEL_MARGIN, y: p.y - ATOM_LABEL_MARGIN },
      { x: p.x + ATOM_LABEL_MARGIN, y: p.y + ATOM_LABEL_MARGIN }
    ]),
    ...annotationPoints
  ];
  return boundsOfPoints(points);
}

// The content of a stored document, in drawing units (its depiction coordinates).
export function documentContentBounds(document: DrawingDocument): ContentBounds | null {
  const coordinateSet = document.depiction.coordinateSet;
  const size = { width: Math.max(1, coordinateSet.width), height: Math.max(1, coordinateSet.height) };
  const toUnits = (point: { readonly x: number; readonly y: number }) => ({
    x: point.x * size.width,
    y: point.y * size.height
  });
  const atoms = documentToAtoms(document);
  const annotations = renderableAnnotations(
    document.annotations ?? [],
    atoms,
    documentToLines(document),
    size,
    size,
    false
  );
  return contentBounds(
    atoms.map((atom) => toUnits(atom.position)),
    annotations.flatMap((annotation) => annotationExtentPoints(annotation, size).map(toUnits))
  );
}

// The stored document with its page extent brought up to date (the same document when it already
// is). A drawing with no page and nothing beyond the default page stays without one.
export function withPageExtent(document: DrawingDocument): DrawingDocument {
  const page = pageOf(document);
  const extent = pageExtentFor(page, documentContentBounds(document));
  const current = extentOf(page);
  const same =
    current.left === extent.left &&
    current.top === extent.top &&
    current.columns === extent.columns &&
    current.rows === extent.rows;
  if (same) return document;
  return { ...document, page: withExtent(page, extent) };
}
